import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Chip,
  Dialog,
  Container,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import Btn from "./Components/Btn";
import MyDataGrid from "./Components/MyDataGrid";
import http, { baseURL } from "./http";
import { getUserInfo } from "./authUtils";
import GlobalLoading from "./Components/loading";
import GlobalAlert from "./Components/alert";

export default function UnverifiedAccounts() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [authAction, setAuthAction] = useState(null);
  const [approveDialogOpened, setApproveDialogOpened] = useState(false);
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false);
  const [item, setItem] = useState({});

  const handleGetDataSource = async () => {
    try {
      GlobalLoading.show();
      const res = await http.get(`/customers/?admin_approved=False`);
      GlobalLoading.hide();
      setEmployees(res.items);
    } catch (e) {
      console.log(e);
      GlobalLoading.hide();
      if (e?.response?.data?.detail) {
        GlobalAlert.error({
          msg: e?.response?.data?.detail,
        });
      } else {
        GlobalAlert.error({
          msg: e.message || "Error !",
        });
      }
    }
  };

  useEffect(() => {
    handleGetDataSource();
  }, []);

  const handleDelete = async () => {
    try {
      const res = await http.delete(`/customers/${item.customer_id}`);
      console.log("handleDelete res", res);
      if (res.status_code === 200) {
        GlobalAlert.success({
          msg: "Delete Success !",
          finish: () => {
            handleGetDataSource();
          },
        });
      } else {
        GlobalAlert.error({
          msg: res.detail || "Delete Failed !",
        });
      }
    } catch (e) {
      GlobalAlert.error({
        msg: "Delete Failed !",
      });
    }
  };

  const employeesColumns = [
    {
      field: "first_name",
      headerName: "Name",
      width: 200,
      editable: false,
      renderCell: (params) => {
        return (
          <span>
            {params?.row?.first_name} {params?.row?.last_name}
          </span>
        );
      },
    },
    {
      field: "member_id",
      headerName: "Member Id",
      width: 200,
      editable: false,
    },
    {
      field: "terms_accepted_status",
      headerName: "Terms",
      width: 160,
      editable: false,
      renderCell: (params) => (
        <TermsStatusChip customer={params?.row} />
      ),
    },
    {
      field: "picture",
      headerName: "Picture",
      width: 200,
      editable: false,
      renderCell: (params) => {
        return (
          <img
            style={{
              maxWidth: 90,
              maxHeight: 90,
              borderRadius: "50%",
              objectFit: "cover",
            }}
            src={params?.row?.image_s3_path}
            alt="Preview"
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      renderCell: (params) => (
        <div>
          <Btn
            variant="contained"
            onClick={() => {
              setSelectedEmployee(params["row"]);
              setApproveDialogOpened(true);
            }}
          >
            Approve
          </Btn>
          <Btn
            sx={{ ml: 1 }}
            variant="outlined"
            color="error"
            onClick={() => {
              setItem(params["row"]);
              setAuthAction("delete");
            }}
          >
            Delete
          </Btn>
        </div>
      ),
    },
  ];

  const getErrorMessage = (error, fallback) => {
    const detail = error?.response?.data?.detail;

    if (Array.isArray(detail)) {
      return detail?.[0]?.msg || fallback;
    }

    return (
      detail ||
      error?.response?.data?.message ||
      error?.message ||
      fallback
    );
  };

  const verifyAdminPassword = async ({ username, password }) => {
    try {
      const payload = new URLSearchParams();
      payload.append("username", username);
      payload.append("password", password);

      await axios.post(`${baseURL}/auth/token`, payload, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
    } catch (e) {
      console.log(e);
      throw new Error(getErrorMessage(e, "Authentication Failed !"));
    }
  };

  const handleApprove = async () => {
    const status = getTermsAcceptedStatus(selectedEmployee);

    if (status !== "acceptedByCustomer" && status !== "pending") {
      throw new Error("This customer cannot be approved from the current terms status.");
    }

    try {
      GlobalLoading.show();
      const override = status === "pending";
      await http.post(`/customers/${selectedEmployee.customer_id}/approve`, {
        override,
      });
      GlobalLoading.hide();
      setEmployees((prev) =>
        prev.filter((employee) => employee.customer_id !== selectedEmployee.customer_id)
      );
      GlobalAlert.success({
        msg: "Approved Success !",
      });
    } catch (e) {
      GlobalLoading.hide();
      console.log(e);
      throw new Error(getErrorMessage(e, "Update Failed !"));
    }
  };

  const handleDeleteAuthSubmit = async ({ username, password }) => {
    await verifyAdminPassword({ username, password });
    setDeleteDialogOpened(true);
    setAuthAction(null);
  };

  const handleApproveSubmit = async ({ username, password }) => {
    await verifyAdminPassword({
      username,
      password,
    });
    await handleApprove();
  };

  return (
    <>
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Box sx={{ height: 400 }}>
          <Typography variant="h5" component="h5">
            Unverified Accounts
          </Typography>
          <MyDataGrid
            rows={employees}
            columns={employeesColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            experimentalFeatures={{ newEditingApi: true }}
            getRowId={(row) => row?.customer_id}
            rowHeight={100}
          />
        </Box>

        <Dialog
          open={Boolean(authAction)}
          onClose={() => setAuthAction(null)}
          maxWidth="sm"
        >
          <AdminCredentialDialog
            defaultUsername={getUserInfo()}
            handleClose={() => setAuthAction(null)}
            handleSubmit={handleDeleteAuthSubmit}
          />
        </Dialog>

        <Dialog
          open={approveDialogOpened}
          onClose={() => setApproveDialogOpened(false)}
          maxWidth="sm"
        >
          <ApproveTermsDialog
            customer={selectedEmployee}
            defaultUsername={getUserInfo()}
            handleClose={() => setApproveDialogOpened(false)}
            handleSubmit={handleApproveSubmit}
          />
        </Dialog>

        <Dialog
          open={deleteDialogOpened}
          onClose={() => setDeleteDialogOpened(false)}
          maxWidth="md"
        >
          <DeleteDialog
            data={selectedEmployee}
            handleClose={() => setDeleteDialogOpened(false)}
            handleSubmit={handleDelete}
          />
        </Dialog>
      </Container>
    </>
  );
}

const TERMS_STATUS_META = {
  acceptedByCustomer: {
    label: "Accepted",
    color: "success",
  },
  adminOverride: {
    label: "Overridden",
    color: "warning",
  },
  pending: {
    label: "Pending",
    color: "default",
  },
};

function getTermsStatusMeta(customer) {
  const normalizedStatus = getTermsAcceptedStatus(customer);
  const fallbackStatus =
    customer?.terms_accepted_status ??
    customer?.termsAcceptedStatus ??
    customer?.terms_accepted;

  return TERMS_STATUS_META[normalizedStatus] || {
    label: fallbackStatus || "Unknown",
    color: "default",
  };
}

function TermsStatusChip({ customer }) {
  const meta = getTermsStatusMeta(customer);

  return <Chip size="small" label={meta.label} color={meta.color} />;
}

function formatTermsTime(time) {
  if (!time) {
    return "";
  }

  const parsed = new Date(time);
  return Number.isNaN(parsed.getTime()) ? time : parsed.toLocaleString();
}

function getTermsAcceptedStatus(customer) {
  const rawStatus =
    customer?.terms_accepted_status ??
    customer?.termsAcceptedStatus ??
    customer?.terms_accepted;

  if (rawStatus === true) {
    return "acceptedByCustomer";
  }

  if (rawStatus === false) {
    return "pending";
  }

  if (typeof rawStatus !== "string") {
    return "";
  }

  const normalized = rawStatus.trim();
  const lower = normalized.toLowerCase();

  if (
    normalized === "acceptedByCustomer" ||
    lower === "accepted_by_customer" ||
    lower === "accepted-by-customer" ||
    lower === "accepted"
  ) {
    return "acceptedByCustomer";
  }

  if (
    normalized === "adminOverride" ||
    lower === "admin_override" ||
    lower === "admin-override" ||
    lower === "overridden"
  ) {
    return "adminOverride";
  }

  if (lower === "pending") {
    return "pending";
  }

  return "";
}

function getTermsAcceptedTime(customer) {
  return (
    customer?.terms_accepted_time ||
    customer?.termsAcceptedTime ||
    customer?.terms_accepted_at ||
    customer?.termsAcceptedAt
  );
}

function AdminCredentialDialog({ defaultUsername, handleClose, handleSubmit }) {
  const [username, setUsername] = useState(defaultUsername || "");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    setUsername(defaultUsername || "");
  }, [defaultUsername]);

  const handleCancel = () => {
    setPassword("");
    setErrorMessage("");
    handleClose();
  };

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage("");

        try {
          await handleSubmit({
            username,
            password,
          });
          setPassword("");
        } catch (error) {
          setErrorMessage(error?.message || "Authentication Failed !");
        } finally {
          setIsLoading(false);
        }
      }}
    >
      <DialogTitle>{"Confirm Admin Credentials"}</DialogTitle>
      <DialogContent sx={{ width: 400 }}>
        <TextField
          style={{ marginTop: 24 }}
          label="Username"
          value={username}
          name="username"
          onChange={(e) => {
            setErrorMessage("");
            setUsername(e.target.value);
          }}
          required
          fullWidth
        />

        <TextField
          style={{ marginTop: 24 }}
          label="Password"
          value={password}
          name="password"
          type="password"
          autoComplete="current-password"
          onChange={(e) => {
            setErrorMessage("");
            setPassword(e.target.value);
          }}
          required
          autoFocus
          fullWidth
        />
        {errorMessage && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary" disabled={isLoading}>
          Cancel
        </Button>
        <Button color="primary" type="submit" disabled={isLoading}>
          Confirm
        </Button>
      </DialogActions>
    </form>
  );
}

function ApproveTermsDialog({
  customer,
  defaultUsername,
  handleClose,
  handleSubmit,
}) {
  const [username, setUsername] = useState(defaultUsername || "");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const termsStatus = getTermsAcceptedStatus(customer);
  const canApprove =
    termsStatus === "acceptedByCustomer" || termsStatus === "pending";
  const isOverride = termsStatus === "pending";
  const submitLabel = canApprove
    ? isOverride
      ? "Override"
      : "Accept"
    : "Unavailable";
  const submitColor = isOverride ? "warning" : "primary";
  const termsAcceptedTime = formatTermsTime(getTermsAcceptedTime(customer));

  useEffect(() => {
    setUsername(defaultUsername || "");
  }, [defaultUsername]);

  const handleSubmitClick = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      await handleSubmit({ username, password });
      setPassword("");
      handleClose();
    } catch (error) {
      setErrorMessage(error?.message || "Update Failed !");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>{"Approve"}</DialogTitle>
      <DialogContent sx={{ width: 400 }}>
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Terms status
          </Typography>
          <TermsStatusChip customer={customer} />
          {termsAcceptedTime && (
            <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
              {termsAcceptedTime}
            </Typography>
          )}
          {!canApprove && (
            <Typography variant="body2" sx={{ mt: 1 }} color="error">
              Missing or unsupported terms_accepted_status.
            </Typography>
          )}
        </Box>
        <TextField
          style={{ marginTop: 24 }}
          label="Username"
          value={username}
          name="username"
          onChange={(e) => {
            setErrorMessage("");
            setUsername(e.target.value);
          }}
          required
          fullWidth
        />
        <TextField
          style={{ marginTop: 24 }}
          label="Password"
          value={password}
          name="password"
          type="password"
          autoComplete="current-password"
          onChange={(e) => {
            setErrorMessage("");
            setPassword(e.target.value);
          }}
          required
          autoFocus
          fullWidth
        />
        {errorMessage && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color={submitColor}
          onClick={handleSubmitClick}
          disabled={isLoading || !canApprove || !username || !password}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </>
  );
}

function DeleteDialog({ data, handleClose, handleSubmit }) {
  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setIsLoading(true);
        handleSubmit();
      }}
    >
      <DialogTitle>{"Confirm Delete ?"}</DialogTitle>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="error"
          type="submit"
          disabled={isLoading}
        >
          Delete
        </Button>
      </DialogActions>
    </form>
  );
}
